<template>
  <dashboard-layout title="Prompt Tests">
    <template #actions>
      <filter-chips v-model="testFilter" :options="testChipOptions" />
      <button class="select-all-btn" @click="toggleSelectAll">{{ selectedTests.length === filteredTests.length ? 'Deselect All' : 'Select All' }}</button>
      <button class="export-btn" v-if="selectedTests.length" @click="exportSelected">Export Selected ({{ selectedTests.length }})</button>
      <!-- <button class="delete-selected-btn" v-if="selectedTests.length" @click="confirmDelete">Delete ({{ selectedTests.length }})</button> -->
    </template>

    <stat-grid>
      <stat-card label="Total tests" :value="tests.length.toLocaleString('en-US')" icon="clipboardcheck" accent />
      <stat-card label="Test 1" :value="test1Count.toLocaleString('en-US')" icon="filetext" />
      <stat-card label="Test 2" :value="test2Count.toLocaleString('en-US')" icon="filetext" />
      <stat-card label="Completed" :value="completedCount.toLocaleString('en-US')" icon="thumbs" />
    </stat-grid>

    <data-panel flush>
      <template #title>Tests <span class="muted">({{ filteredTests.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading tests…" />

      <empty-state v-else-if="filteredTests.length === 0" title="No test results yet" />

      <div v-else class="tests-list">
        <!-- Test Cards -->
        <div v-for="test in filteredTests" :key="test.id" class="test-card" :class="{ expanded: expandedId === test.id }" @click="toggleExpand(test.id)">
          <!-- Card Header -->
          <div class="test-header">
            <input type="checkbox" class="select-check" :checked="selectedTests.includes(test.id)" @click.stop="toggleSelect(test.id)">
            <div class="test-info">
              <span class="test-name">{{ test.userName || 'Anonymous' }}</span>
              <span class="test-uid">{{ test.userId }}</span>
            </div>
            <span class="test-number-badge" :class="'test-' + (test.testNumber || 1)">T{{ test.testNumber || 1 }}</span>
            <div class="test-meta">
              <span class="test-date">{{ formatDate(test.createdAt) }}</span>
              <status-badge :variant="test.completedAt ? 'success' : 'warning'" :label="test.completedAt ? 'Completed' : 'Incomplete'" />
              <span class="flag-dot transcript-dot" v-if="test.groupedTranscript" title="Has transcript"></span>
              <span class="flag-dot link-dot" v-if="test.userbrainLink" title="Has Userbrain link"></span>
            </div>
            <div class="test-scores" v-if="test.wrapUp">
              <div class="mini-score"><span class="mini-label">Use Again</span><span class="mini-val">{{ test.wrapUp.likelyToUse }}</span></div>
              <div class="mini-score"><span class="mini-label">Real Astrologer</span><span class="mini-val">{{ test.wrapUp.realAstrologer }}</span></div>
              <div class="mini-score" v-if="test.wrapUp.likelyToPay !== undefined"><span class="mini-label">Pay</span><span class="mini-val">{{ test.wrapUp.likelyToPay }}</span></div>
            </div>
            <span class="expand-arrow">{{ expandedId === test.id ? '▼' : '▶' }}</span>
          </div>

          <!-- Expanded Detail -->
          <div v-if="expandedId === test.id" class="test-detail" @click.stop>
            <div class="detail-actions">
              <button class="export-btn" @click="exportTest(test)">Export JSON</button>
              <button class="export-btn transcript-btn" @click="openTranscriptModal(test)">Add Transcript</button>
              <button class="export-btn link-btn" @click="openLinkModal(test)">{{ test.userbrainLink ? 'Edit Link' : 'Add Userbrain Link' }}</button>
              <a v-if="test.userbrainLink" class="export-btn userbrain-btn" :href="test.userbrainLink" target="_blank" @click.stop>Open Userbrain</a>
            </div>

            <!-- Context -->
            <div class="detail-section" v-if="test.context">
              <h3>Context</h3>
              <div class="detail-row">
                <span class="detail-label">Interest Level</span>
                <span class="detail-value">{{ test.context.interestLevel }}/10</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Wants</span>
                <span class="detail-value">{{ test.context.whatTheyWant }}</span>
              </div>
            </div>

            <!-- Question Results -->
            <div class="detail-section" v-if="test.questionResults && test.questionResults.length">
              <h3>Questions ({{ test.questionResults.length }})</h3>
              <div v-for="(qr, i) in test.questionResults" :key="i" class="question-result">
                <div class="qr-header">
                  <span class="qr-num">Q{{ i + 1 }}</span>
                  <span class="qr-question">{{ qr.questionText }}</span>
                  <span class="qr-free" v-if="qr.isFreeQuestion">Free</span>
                  <span class="qr-followup-badge" v-if="qr.choseFollowUp">{{ qr.followUps ? qr.followUps.length : 0 }} follow-up</span>
                  <span class="qr-no-followup-badge" v-else-if="qr.choseFollowUp === false">no follow-up</span>
                </div>

                <!-- Answer Preview -->
                <div class="qr-answer" v-if="qr.answer" v-html="formatAnswer(qr.answer)"></div>

                <!-- Ratings -->
                <div class="qr-ratings" v-if="qr.feedback">
                  <div v-for="(val, key) in qr.feedback.ratings" :key="key" class="rating-chip">
                    <span class="rc-label">{{ formatRatingKey(key) }}</span>
                    <span class="rc-val" :class="scoreClass(val)">{{ val }}</span>
                  </div>
                </div>

                <!-- Flags -->
                <div class="qr-flags" v-if="qr.feedback && qr.feedback.flags && qr.feedback.flags.length">
                  <span v-for="f in qr.feedback.flags" :key="f" class="flag-chip">{{ f }}</span>
                </div>

                <!-- Comment -->
                <div class="qr-comment" v-if="qr.feedback && qr.feedback.comment">
                  "{{ qr.feedback.comment }}"
                </div>

                <!-- Follow-ups (Test 2) -->
                <div v-if="qr.followUps && qr.followUps.length" class="qr-followups">
                  <span class="qr-followups-label">Follow-ups ({{ qr.followUps.length }})</span>
                  <div v-for="(fu, fi) in qr.followUps" :key="fi" class="qr-followup-item">
                    <div class="qr-fu-q">{{ fu.question }}</div>
                    <div class="qr-fu-a" v-html="formatAnswer(fu.answer)"></div>
                  </div>
                </div>

                <!-- Mini Eval (Test 2) -->
                <div v-if="qr.miniEval" class="qr-minieval">
                  <span class="qr-minieval-label">Follow-up Evaluation</span>
                  <div class="qr-minieval-scores">
                    <span>Satisfaction: <strong :class="scoreClass(qr.miniEval.satisfaction)">{{ qr.miniEval.satisfaction }}</strong></span>
                    <span>Additional Help: <strong :class="scoreClass(qr.miniEval.additionalHelp)">{{ qr.miniEval.additionalHelp }}</strong></span>
                  </div>
                  <div v-if="qr.miniEval.comment" class="qr-minieval-comment">"{{ qr.miniEval.comment }}"</div>
                </div>

                <!-- Transcript Note -->
                <div class="qr-transcript" v-if="getTranscriptForQuestion(test, i)">
                  <span class="qr-transcript-label">Verbal Reaction</span>
                  <div class="qr-transcript-text">{{ getTranscriptForQuestion(test, i) }}</div>
                </div>
              </div>
            </div>

            <!-- Wrap Up -->
            <div class="detail-section" v-if="test.wrapUp">
              <h3>Wrap Up</h3>
              <div class="detail-row">
                <span class="detail-label">Use Again</span>
                <span class="detail-value" :class="scoreClass(test.wrapUp.likelyToUse)">{{ test.wrapUp.likelyToUse }}/10</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Real Astrologer</span>
                <span class="detail-value" :class="scoreClass(test.wrapUp.realAstrologer)">{{ test.wrapUp.realAstrologer }}/10</span>
              </div>
              <div class="detail-row" v-if="test.wrapUp.likelyToPay !== undefined">
                <span class="detail-label">Likely to Pay</span>
                <span class="detail-value" :class="scoreClass(test.wrapUp.likelyToPay)">{{ test.wrapUp.likelyToPay }}/10</span>
              </div>
              <div class="detail-row" v-if="test.wrapUp.strongest">
                <span class="detail-label">Strongest</span>
                <span class="detail-value">{{ test.wrapUp.strongest }}</span>
              </div>
              <div class="detail-row" v-if="test.wrapUp.weakest">
                <span class="detail-label">Weakest</span>
                <span class="detail-value">{{ test.wrapUp.weakest }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </data-panel>
  </dashboard-layout>

  <!-- Delete Modal -->
  <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
    <div class="modal-box" @click.stop>
      <h2>Delete Test</h2>
      <p>Enter password to delete this test.</p>
      <input v-model="deletePassword" type="password" placeholder="Password" class="modal-input" @keyup.enter="executeDelete">
      <div class="modal-actions">
        <button class="cancel-btn" @click="showDeleteModal = false">Cancel</button>
        <button class="delete-btn" @click="executeDelete" :disabled="!deletePassword">Delete</button>
      </div>
    </div>
  </div>

  <!-- Userbrain Link Modal -->
  <div v-if="showLinkModal" class="modal-overlay" @click="showLinkModal = false">
    <div class="modal-box" @click.stop>
      <h2>Userbrain Link</h2>
      <input v-model="linkValue" type="url" placeholder="https://app.userbrain.com/..." class="modal-input">
      <div class="modal-actions">
        <button class="cancel-btn" @click="showLinkModal = false">Cancel</button>
        <button class="export-btn" @click="saveLink" :disabled="!linkValue.trim()">Save</button>
      </div>
    </div>
  </div>

  <!-- Transcript Modal -->
  <div v-if="showTranscriptModal" class="modal-overlay" @click="showTranscriptModal = false">
    <div class="modal-box" @click.stop>
      <h2>Add Transcript</h2>
      <p>Paste the full session transcript. AI will group it by question.</p>
      <textarea v-model="transcriptText" placeholder="Paste transcript here..." rows="12" :disabled="transcriptProcessing"></textarea>
      <div class="modal-actions">
        <button class="cancel-btn" @click="showTranscriptModal = false" :disabled="transcriptProcessing">Cancel</button>
        <button class="export-btn" @click="processTranscript" :disabled="!transcriptText.trim() || transcriptProcessing">
          {{ transcriptProcessing ? 'Processing...' : 'Process with AI' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { doc, updateDoc } from 'firebase/firestore'
import { initFirebase } from '../firebase'
import { PromptTestService } from '../services/promptTestService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const { db } = initFirebase()
const AI_ENDPOINT = 'https://us-central1-astroai-200db.cloudfunctions.net/marketingReply'

export default {
  name: 'PromptTestDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, FilterChips, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const service = new PromptTestService()
    const tests = ref([])
    const loading = ref(true)
    const expandedId = ref(null)
    const testFilter = ref('all')

    const loadTests = async () => {
      loading.value = true
      try {
        const all = await service.getAllTests()
        tests.value = all.filter(t => !t.isDeleted)
      } catch (e) {
        console.error('Error loading tests:', e)
      } finally {
        loading.value = false
      }
    }

    const filteredTests = computed(() => {
      if (testFilter.value === 'all') return tests.value
      return tests.value.filter(t => (t.testNumber || 1) === testFilter.value)
    })

    const test1Count = computed(() => tests.value.filter(t => (t.testNumber || 1) === 1).length)
    const test2Count = computed(() => tests.value.filter(t => (t.testNumber || 1) === 2).length)
    const completedCount = computed(() => tests.value.filter(t => t.completedAt).length)

    const testChipOptions = computed(() => [
      { value: 'all', label: 'All', count: tests.value.length },
      { value: 1, label: 'Test 1', count: test1Count.value },
      { value: 2, label: 'Test 2', count: test2Count.value }
    ])

    const toggleExpand = (id) => {
      expandedId.value = expandedId.value === id ? null : id
    }

    const formatDate = (ts) => {
      if (!ts) return '-'
      const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }

    const truncate = (text, len) => {
      if (!text) return ''
      return text.length > len ? text.substring(0, len) + '...' : text
    }

    const formatAnswer = (text) => {
      if (!text) return ''
      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>')
    }

    const formatRatingKey = (key) => {
      const map = { specific: 'Specific', accurate: 'Accurate', clear: 'Clear', useful: 'Useful', tone: 'Tone', astrological: 'Astro', overall: 'Overall' }
      return map[key] || key
    }

    const getTranscriptForQuestion = (test, questionIndex) => {
      if (!test.groupedTranscript) return null
      const group = test.groupedTranscript[questionIndex]
      if (!group || !group.verbatim || group.verbatim === 'No clear reaction captured.') return null
      return group.verbatim
    }

    const scoreClass = (val) => {
      if (val === null || val === undefined) return ''
      if (val >= 8) return 'score-high'
      if (val >= 5) return 'score-mid'
      return 'score-low'
    }

    const selectedTests = ref([])
    const showDeleteModal = ref(false)
    const deletePassword = ref('')
    const DELETE_HASH = 'a5f117dab01837a8abc8555e4587f344767375139f2a1af8429ff1af8be6ce4f'

    const sha256 = async (text) => {
      const encoder = new TextEncoder()
      const data = encoder.encode(text)
      const hash = await crypto.subtle.digest('SHA-256', data)
      return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
    }

    const toggleSelectAll = () => {
      if (selectedTests.value.length === filteredTests.value.length) {
        selectedTests.value = []
      } else {
        selectedTests.value = filteredTests.value.map(t => t.id)
      }
    }

    const toggleSelect = (id) => {
      const idx = selectedTests.value.indexOf(id)
      if (idx > -1) selectedTests.value.splice(idx, 1)
      else selectedTests.value.push(id)
    }

    const confirmDelete = () => {
      deletePassword.value = ''
      showDeleteModal.value = true
    }

    const executeDelete = async () => {
      if (!deletePassword.value || !selectedTests.value.length) return
      const hash = await sha256(deletePassword.value)
      if (hash !== DELETE_HASH) {
        alert('Incorrect password.')
        return
      }
      try {
        await Promise.all(selectedTests.value.map(id => updateDoc(doc(db, 'promptTests', id), { isDeleted: true })))
        tests.value = tests.value.filter(t => !selectedTests.value.includes(t.id))
        if (selectedTests.value.includes(expandedId.value)) expandedId.value = null
        selectedTests.value = []
        showDeleteModal.value = false
      } catch (e) {
        alert('Delete failed: ' + e.message)
      }
    }

    const showLinkModal = ref(false)
    const linkValue = ref('')
    const linkTestId = ref(null)

    const openLinkModal = (test) => {
      linkTestId.value = test.id
      linkValue.value = test.userbrainLink || ''
      showLinkModal.value = true
    }

    const saveLink = async () => {
      if (!linkValue.value.trim() || !linkTestId.value) return
      await updateDoc(doc(db, 'promptTests', linkTestId.value), { userbrainLink: linkValue.value.trim() })
      const test = tests.value.find(t => t.id === linkTestId.value)
      if (test) test.userbrainLink = linkValue.value.trim()
      showLinkModal.value = false
    }

    const showTranscriptModal = ref(false)
    const transcriptText = ref('')
    const transcriptProcessing = ref(false)
    const transcriptTestId = ref(null)

    const openTranscriptModal = (test) => {
      transcriptTestId.value = test.id
      transcriptText.value = ''
      showTranscriptModal.value = true
    }

    const processTranscript = async () => {
      if (!transcriptText.value.trim() || !transcriptTestId.value) return
      transcriptProcessing.value = true

      const test = tests.value.find(t => t.id === transcriptTestId.value)
      if (!test) return

      const questions = (test.questionResults || []).map((qr, i) => `Q${i + 1}: ${qr.questionText}`).join('\n')

      const systemPrompt = `You are analyzing a screen recorded Astro Chat evaluation test. You will receive:
- The list of test questions shown to the tester
- The full transcript of the tester's spoken words during the test

Important context: There is no moderator. The tester is reading the on-screen questions and the AI answers out loud, while also adding their own reactions and comments during the test.

Your job is to extract only the tester's own reactions and comments for each question.

Very important rules:
- Do NOT copy the on-screen question text or AI answer text into the "verbatim" field.
- Only extract the tester's own spoken reactions, opinions, judgments, and commentary.
- If the tester repeats or paraphrases part of the question or answer before reacting, keep only the reaction part.
- Map each reaction to the most relevant test question.
- If a tester reaction clearly belongs to a question, assign it there.
- If a reaction is ambiguous, make the best judgment based on surrounding context.
- If no meaningful reaction is captured for a question, write: "No clear reaction captured."
- Keep each extracted reaction concise but complete.
- Preserve strong wording when it matters, such as "generic", "vague", "accurate", "useful", "not personal", "helpful", "confusing", "too broad", "too dramatic".

Return ONLY a valid JSON array, no markdown, no explanation, no extra text.

Format:
[
  {"question": "Q1: Who am I, really...", "verbatim": "tester's exact reaction words"},
  {"question": "Q2: Why have I been...", "verbatim": "tester's exact reaction words"}
]

Test questions shown during the session:
${questions}`

      try {
        const response = await fetch(AI_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: transcriptText.value }
            ],
            model: 'gemini-2.5-flash'
          })
        })

        const data = await response.json()
        if (data.success && data.reply) {
          let grouped
          try {
            const cleaned = data.reply.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
            grouped = JSON.parse(cleaned)
          } catch {
            grouped = [{ question: 'Raw AI Response', verbatim: data.reply }]
          }

          // Save to Firestore
          await updateDoc(doc(db, 'promptTests', transcriptTestId.value), { groupedTranscript: grouped })

          // Update local state
          test.groupedTranscript = grouped
          showTranscriptModal.value = false
        } else {
          alert('AI processing failed: ' + (data.error || 'Unknown error'))
        }
      } catch (e) {
        console.error('Transcript processing error:', e)
        alert('Error: ' + e.message)
      } finally {
        transcriptProcessing.value = false
      }
    }

    const exportSelected = () => {
      const selected = tests.value.filter(t => selectedTests.value.includes(t.id)).map(test => {
        const exportData = { ...test }
        if (exportData.questionResults && exportData.groupedTranscript) {
          exportData.questionResults = exportData.questionResults.map((qr, i) => ({
            ...qr,
            verbalReaction: exportData.groupedTranscript[i]?.verbatim || null
          }))
        }
        return exportData
      })
      const data = JSON.stringify(selected, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `prompt_tests_${selected.length}_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const exportTest = (test) => {
      const exportData = { ...test }
      if (exportData.questionResults && exportData.groupedTranscript) {
        exportData.questionResults = exportData.questionResults.map((qr, i) => ({
          ...qr,
          verbalReaction: exportData.groupedTranscript[i]?.verbatim || null
        }))
      }
      const data = JSON.stringify(exportData, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `prompt_test_${test.userName || test.userId}_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    onMounted(loadTests)

    return { tests, filteredTests, testFilter, testChipOptions, test1Count, test2Count, completedCount, loading, expandedId, toggleExpand, formatDate, truncate, formatAnswer, formatRatingKey, scoreClass, getTranscriptForQuestion, exportSelected, exportTest, selectedTests, toggleSelectAll, toggleSelect, showDeleteModal, deletePassword, confirmDelete, executeDelete, showLinkModal, linkValue, openLinkModal, saveLink, showTranscriptModal, transcriptText, transcriptProcessing, openTranscriptModal, processTranscript }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.select-all-btn { padding: 8px 16px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; color: #fff; font-size: 13px; cursor: pointer; transition: all 0.2s; }
.select-all-btn:hover { background: rgba(255,255,255,0.1); }
.export-btn { padding: 8px 16px; background: var(--primary-color); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.export-btn:hover { filter: brightness(1.15); }
.export-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Test List */
.tests-list { display: flex; flex-direction: column; gap: 8px; padding: 12px; }

/* Test Card */
.test-card { background: var(--surface-dark, rgba(26,27,46,0.95)); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; cursor: pointer; transition: all 0.2s; overflow: hidden; }
.test-card:hover { border-color: rgba(108,99,255,0.3); }
.test-card.expanded { border-color: rgba(108,99,255,0.2); }

.test-header { display: flex; align-items: center; gap: 16px; padding: 14px 18px; }
.test-info { display: flex; flex-direction: column; gap: 2px; min-width: 140px; }
.test-name { font-size: 14px; font-weight: 600; color: #fff; }
.test-uid { font-size: 10px; font-family: monospace; color: rgba(255,255,255,0.3); }
.test-meta { display: flex; gap: 10px; align-items: center; }
.test-date { font-size: 12px; color: rgba(255,255,255,0.4); }
.test-scores { display: flex; gap: 12px; margin-left: auto; }
.mini-score { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.mini-label { font-size: 9px; color: rgba(255,255,255,0.3); text-transform: uppercase; }
.mini-val { font-size: 16px; font-weight: 700; color: #a29bfe; }
.flag-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.transcript-dot { background: #fbbf24; }
.link-dot { background: #22d3ae; }
.select-check { width: 16px; height: 16px; accent-color: #6c5ce7; cursor: pointer; flex-shrink: 0; }
.delete-selected-btn { padding: 8px 16px; background: rgba(239,68,68,0.15); border: none; border-radius: 8px; color: #f87171; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.delete-selected-btn:hover { background: rgba(239,68,68,0.25); }
.delete-btn { padding: 8px 16px; background: rgba(239,68,68,0.15); border: none; border-radius: 8px; color: #f87171; font-size: 13px; font-weight: 600; cursor: pointer; }
.delete-btn:hover { background: rgba(239,68,68,0.25); }
.delete-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.expand-arrow { font-size: 10px; color: rgba(255,255,255,0.3); margin-left: 8px; }

/* Expanded Detail */
.test-detail { padding: 0 18px 18px; display: flex; flex-direction: column; gap: 20px; cursor: default; }
.detail-section { border-top: 1px solid rgba(255,255,255,0.05); padding-top: 16px; }
.detail-section h3 { font-size: 13px; font-weight: 700; color: #a29bfe; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 12px; }
.detail-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 6px 0; gap: 16px; }
.detail-label { font-size: 13px; color: rgba(255,255,255,0.4); flex-shrink: 0; }
.detail-value { font-size: 13px; color: #fff; text-align: right; }

/* Question Results */
.question-result { padding: 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 10px; margin-bottom: 10px; display: flex; flex-direction: column; gap: 10px; }
.qr-header { display: flex; align-items: center; gap: 8px; }
.qr-num { font-size: 11px; font-weight: 700; color: #a29bfe; background: rgba(108,99,255,0.1); padding: 2px 8px; border-radius: 6px; }
.qr-question { font-size: 13px; color: #fff; font-weight: 500; flex: 1; }
.qr-free { font-size: 10px; color: #fbbf24; background: rgba(251,191,36,0.1); padding: 2px 6px; border-radius: 4px; }
.qr-followup-badge { font-size: 10px; color: #22d3ae; background: rgba(34,211,174,0.1); padding: 2px 8px; border-radius: 4px; font-weight: 600; }
.qr-no-followup-badge { font-size: 10px; color: rgba(255,255,255,0.25); background: rgba(255,255,255,0.03); padding: 2px 8px; border-radius: 4px; }
.qr-answer { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.7; padding: 14px 16px; background: rgba(255,255,255,0.02); border-radius: 8px; white-space: pre-wrap; word-wrap: break-word; }
.qr-answer :deep(strong) { color: #a29bfe; }
.qr-ratings { display: flex; gap: 6px; flex-wrap: wrap; }
.rating-chip { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 6px 10px; background: rgba(255,255,255,0.03); border-radius: 8px; border: 1px solid rgba(255,255,255,0.05); }
.rc-label { font-size: 9px; color: rgba(255,255,255,0.3); text-transform: uppercase; }
.rc-val { font-size: 16px; font-weight: 700; }
.qr-flags { display: flex; gap: 4px; flex-wrap: wrap; }
.flag-chip { font-size: 10px; padding: 3px 8px; border-radius: 6px; background: rgba(251,191,36,0.08); color: #fbbf24; }
.qr-comment { font-size: 12px; color: rgba(255,255,255,0.5); font-style: italic; padding: 8px 12px; background: rgba(108,99,255,0.04); border-radius: 8px; border-left: 3px solid rgba(108,99,255,0.2); }
.qr-transcript { padding: 10px 14px; background: rgba(251,191,36,0.04); border-radius: 8px; border-left: 3px solid rgba(251,191,36,0.3); }
.qr-transcript-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #fbbf24; display: block; margin-bottom: 4px; }
.qr-transcript-text { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.6; white-space: pre-wrap; }

/* Score Colors */
.score-high { color: #22d3ae; }
.score-mid { color: #fbbf24; }
.score-low { color: #f87171; }

/* Detail Actions */
.detail-actions { display: flex; gap: 8px; }
.transcript-btn { background: rgba(251,191,36,0.15); color: #fbbf24; }
.transcript-btn:hover { background: rgba(251,191,36,0.25); }
.link-btn { background: rgba(96,165,250,0.15); color: #60a5fa; }
.link-btn:hover { background: rgba(96,165,250,0.25); }
.userbrain-btn { background: rgba(34,211,174,0.15); color: #22d3ae; text-decoration: none; display: inline-flex; align-items: center; }
.userbrain-btn:hover { background: rgba(34,211,174,0.25); }
.modal-input { padding: 12px 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; color: #fff; font-size: 13px; font-family: inherit; width: 100%; box-sizing: border-box; }
.modal-input:focus { outline: none; border-color: rgba(255,255,255,0.25); }

/* Transcript Groups */
.transcript-group { padding: 12px 14px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 10px; margin-bottom: 8px; }
.tg-question { font-size: 12px; font-weight: 600; color: #a29bfe; margin-bottom: 6px; }
.tg-text { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.6; white-space: pre-wrap; }

/* Modal */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-box { background: var(--surface-dark, #1a1a2e); border: 1px solid rgba(255,255,255,0.08); border-radius: 16px; padding: 24px; width: 90%; max-width: 600px; display: flex; flex-direction: column; gap: 14px; }
.modal-box h2 { margin: 0; font-size: 18px; color: #fff; }
.modal-box p { margin: 0; font-size: 13px; color: rgba(255,255,255,0.5); }
.modal-box textarea { padding: 14px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; color: #fff; font-size: 13px; font-family: inherit; resize: vertical; }
.modal-box textarea:focus { outline: none; border-color: rgba(255,255,255,0.25); }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.cancel-btn { padding: 8px 16px; background: rgba(255,255,255,0.06); border: none; border-radius: 8px; color: rgba(255,255,255,0.6); font-size: 13px; cursor: pointer; }
.cancel-btn:hover { background: rgba(255,255,255,0.1); }

/* Test Number Badge */
.test-number-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 6px; flex-shrink: 0; }
.test-number-badge.test-1 { background: rgba(108,99,255,0.1); color: #a29bfe; }
.test-number-badge.test-2 { background: rgba(34,211,174,0.1); color: #22d3ae; }

/* Follow-ups display */
.qr-followups { padding: 12px; background: rgba(108,99,255,0.03); border-radius: 8px; border-left: 3px solid rgba(108,99,255,0.2); display: flex; flex-direction: column; gap: 10px; }
.qr-followups-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #a29bfe; }
.qr-followup-item { display: flex; flex-direction: column; gap: 6px; padding: 8px 0; border-top: 1px solid rgba(255,255,255,0.04); }
.qr-followup-item:first-of-type { border-top: none; }
.qr-fu-q { font-size: 12px; color: #a29bfe; font-weight: 600; }
.qr-fu-a { font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.6; }
.qr-fu-a :deep(strong) { color: #a29bfe; }

/* Mini Eval display */
.qr-minieval { padding: 10px 14px; background: rgba(34,211,174,0.04); border-radius: 8px; border-left: 3px solid rgba(34,211,174,0.3); display: flex; flex-direction: column; gap: 6px; }
.qr-minieval-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #22d3ae; }
.qr-minieval-scores { display: flex; gap: 16px; font-size: 12px; color: rgba(255,255,255,0.5); }
.qr-minieval-scores strong { font-size: 14px; }
.qr-minieval-comment { font-size: 12px; color: rgba(255,255,255,0.5); font-style: italic; }

@media (max-width: 768px) {
  .test-header { flex-wrap: wrap; }
  .test-scores { margin-left: 0; }
  .qr-ratings { gap: 4px; }
  .rating-chip { padding: 4px 8px; }
}
</style>
